<template>
    <v-container fluid class="pa-4">
        <v-row class="mb-2">
            <v-col cols="12">
                <h1 class="text-h5 font-weight-bold">Shift settings</h1>
                <p class="text-body-2 text-medium-emphasis mt-1">
                    How the SimpliGPS Driver app reminds drivers to clock out, and how shifts are closed when they forget
                </p>
            </v-col>
        </v-row>

        <v-row v-if="isAdmin" class="mb-2">
            <v-col cols="12" sm="6" md="4">
                <company-select v-model="selectedCompany" :items="companies" label="Company"
                                density="compact" variant="outlined" hide-details :disabled="saving"
                                @update:model-value="load"/>
            </v-col>
        </v-row>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"/>

        <v-alert v-if="isAdmin && !selectedCompany" type="info" variant="tonal" density="compact">
            Select a company to view and edit its shift settings.
        </v-alert>

        <v-alert v-else-if="loadError" type="error" variant="tonal" density="compact">
            {{ loadError }}
            <template #append>
                <v-btn size="small" variant="text" @click="load">Retry</v-btn>
            </template>
        </v-alert>

        <v-row v-else-if="settings">
            <v-col cols="12" md="8" lg="6">
                <v-card elevation="2" rounded="lg" class="pa-4">
                    <h3 class="text-subtitle-1 font-weight-bold mb-1">A shift with no sign of work</h3>
                    <p class="text-body-2 text-medium-emphasis mb-4">
                        When a driver never clocks out, the system ends the shift at their last sign of work:
                        a trip, a stop, an inspection or a break. If there is none at all, it ends the shift
                        this many hours after clock-in and flags it for you to review.
                    </p>

                    <div class="d-flex align-center ga-4 mb-1">
                        <v-slider v-model="hours" :min="1" :max="settings.stale_after_hours" :step="1"
                                  color="primary" hide-details class="flex-grow-1" thumb-label
                                  aria-label="Hours after clock-in"/>
                        <v-text-field v-model.number="hours" type="number" :min="1"
                                      :max="settings.stale_after_hours" suffix="hours" density="compact"
                                      variant="outlined" hide-details style="max-width: 130px"
                                      :error="!hoursValid"/>
                    </div>
                    <div class="text-caption mb-4" :class="hoursValid ? 'text-medium-emphasis' : 'text-error'">
                        <template v-if="hoursValid">
                            Example: clock-in at 7:00 AM with nothing after it ends at {{ exampleEnd }}.
                            Default {{ settings.no_activity_hours_default }} hours.
                        </template>
                        <template v-else>
                            Enter a whole number of hours from 1 to {{ settings.stale_after_hours }}.
                        </template>
                    </div>

                    <template v-if="hasWarn">
                        <v-divider class="mb-4"/>

                        <h3 class="text-subtitle-1 font-weight-bold mb-1">Remind the driver to clock out</h3>
                        <p class="text-body-2 text-medium-emphasis mb-4">
                            The driver's phone shows a notification this many hours after clock-in. It is only a
                            reminder: it does not end or change the shift.
                        </p>

                        <div class="d-flex align-center ga-4 mb-1">
                            <v-slider v-model="warnHours" :min="1" :max="settings.stale_after_hours" :step="1"
                                      color="primary" hide-details class="flex-grow-1" thumb-label
                                      aria-label="Hours on shift before the reminder"/>
                            <v-text-field v-model.number="warnHours" type="number" :min="1"
                                          :max="settings.stale_after_hours" suffix="hours" density="compact"
                                          variant="outlined" hide-details style="max-width: 130px"
                                          :error="!warnValid"/>
                        </div>
                        <div class="text-caption mb-4" :class="warnValid ? 'text-medium-emphasis' : 'text-error'">
                            <template v-if="warnValid">
                                Default {{ settings.warn_after_hours_default }} hours.
                            </template>
                            <template v-else>
                                Enter a whole number of hours from 1 to {{ settings.stale_after_hours }}.
                            </template>
                        </div>
                    </template>

                    <v-divider class="mb-4"/>

                    <h3 class="text-subtitle-1 font-weight-bold mb-2">Always on</h3>
                    <div class="fact">
                        <v-icon size="18" color="primary">mdi-timer-outline</v-icon>
                        <span>A shift still open {{ settings.stale_after_hours }} hours after clock-in is closed
                            automatically.</span>
                    </div>
                    <div class="fact">
                        <v-icon size="18" color="primary">mdi-clock-check-outline</v-icon>
                        <span>A late clock-out from the driver is applied on its own when it is no more than
                            {{ settings.correction_grace_minutes }} minutes after the end the system chose
                            and reaches us within two days. Anything else comes to you for review.</span>
                    </div>
                    <div class="fact">
                        <v-icon size="18" color="primary">mdi-account-check-outline</v-icon>
                        <span>Shifts flagged for review are listed on the
                            <router-link :to="{name: 'fleet/shifts', query: {review: '1'}}">Shifts page</router-link>
                            and on each driver's Shifts tab.</span>
                    </div>

                    <div class="d-flex justify-end ga-2 mt-4">
                        <v-btn variant="text" :disabled="!dirty || saving" @click="reset">
                            Undo
                        </v-btn>
                        <v-btn color="primary" variant="flat" :loading="saving" :disabled="!dirty || !formValid"
                               @click="save">Save</v-btn>
                    </div>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import CompanySelect from "@/components/globals/company-select.vue";

const {user} = useAuthStore();
const {showSnackbar} = useSnackbarStore();
const isAdmin = computed(() => user?.type === "admin");

const companies = ref([]);
const selectedCompany = ref(isAdmin.value ? null : user?.company?.id);
const settings = ref(null);
const hours = ref(12);
const warnHours = ref(8);
const loading = ref(false);
const saving = ref(false);
const loadError = ref("");

const validHours = (v) => Number.isInteger(Number(v))
    && Number(v) >= 1 && Number(v) <= (settings.value?.stale_after_hours ?? 16);
const hoursValid = computed(() => validHours(hours.value));
// An API without the reminder setting does not send it; the section is hidden.
const hasWarn = computed(() => Number.isInteger(settings.value?.warn_after_hours));
const warnValid = computed(() => !hasWarn.value || validHours(warnHours.value));
const formValid = computed(() => hoursValid.value && warnValid.value);
const dirty = computed(() => settings.value && (Number(hours.value) !== settings.value.no_activity_hours
    || (hasWarn.value && Number(warnHours.value) !== settings.value.warn_after_hours)));

const reset = () => {
    hours.value = settings.value.no_activity_hours;
    warnHours.value = settings.value.warn_after_hours ?? settings.value.warn_after_hours_default ?? 8;
};

const exampleEnd = computed(() => {
    const total = 7 + Number(hours.value);
    const h = total % 24;
    const label = `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`;
    return total >= 24 ? `${label} the next day` : label;
});

const params = () => (isAdmin.value && selectedCompany.value ? {company: selectedCompany.value} : {});

// Guards against an older company's settings landing after a newer one's,
// which an admin could then save to the wrong company.
let requestSeq = 0;

const load = () => {
    if (isAdmin.value && !selectedCompany.value) return;
    const seq = ++requestSeq;
    loading.value = true;
    loadError.value = "";
    settings.value = null;
    send("get", "fleet/duty/settings", params())
        .then((r) => {
            if (seq !== requestSeq) return;
            settings.value = r.data.data;
            reset();
        })
        .catch((e) => {
            if (seq !== requestSeq) return;
            loadError.value = e?.response?.status === 403 || /not permitted/i.test(e?.response?.data?.message || "")
                ? "You don't have access to shift settings. Ask your administrator."
                : (e?.response?.data?.message || "Shift settings could not be loaded.");
        })
        .finally(() => {
            if (seq === requestSeq) loading.value = false;
        });
};

const save = () => {
    if (!formValid.value) return;
    const seq = requestSeq;
    saving.value = true;
    // Only what changed, so two managers editing different settings at once
    // do not put back each other's old value.
    const body = {...params()};
    if (Number(hours.value) !== settings.value.no_activity_hours) body.no_activity_hours = Number(hours.value);
    if (hasWarn.value && Number(warnHours.value) !== settings.value.warn_after_hours) {
        body.warn_after_hours = Number(warnHours.value);
    }
    send("put", "fleet/duty/settings", body)
        .then((r) => {
            // Saved, but the page has moved to another company since: do not
            // show this company's value under that one.
            if (seq !== requestSeq) return;
            settings.value = r.data.data;
            reset();
            showSnackbar(r.data.message || "Shift settings saved");
        })
        .catch((e) => {
            const errors = e?.response?.data?.errors;
            const first = errors && Object.values(errors)[0];
            showSnackbar((Array.isArray(first) && first[0]) || e?.response?.data?.message
                || "Could not save shift settings", "error");
        })
        .finally(() => {
            saving.value = false;
        });
};

onMounted(() => {
    load();
    if (isAdmin.value) {
        send("get", "companies", {list: true})
            .then((r) => {
                companies.value = r?.data?.data ?? [];
            })
            .catch((e) => console.warn("Could not load companies filter", e));
    }
});
</script>

<style scoped>
.fact {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 4px 0;
    font-size: 0.875rem;
}

.fact .v-icon {
    margin-top: 2px;
}
</style>
